import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const ThemeContext = createContext(undefined);

const STORAGE_KEY = "app_theme";
const DARK = "dark";
const LIGHT = "light";

/**
 * Reads the initial theme from localStorage or OS preference.
 * Falls back to "light" when neither source provides a value.
 */
function getInitialTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === DARK || stored === LIGHT) return stored;
  } catch {
    // localStorage may be unavailable (private browsing, SSR, etc.)
  }

  if (
    typeof window !== "undefined" &&
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  ) {
    return DARK;
  }

  return LIGHT;
}

/**
 * ThemeProvider — wraps the app and manages the current theme.
 *
 * • Reads saved preference from localStorage key "app_theme".
 * • Falls back to the OS `prefers-color-scheme` media query.
 * • Sets `data-theme` attribute on <html> for CSS custom-property switching.
 * • Persists every user choice back to localStorage.
 */
export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(getInitialTheme);

  // Sync data-theme attribute and localStorage whenever theme changes
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // ignore write errors
    }
  }, [theme]);

  // Listen for OS-level preference changes when no explicit user choice exists
  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => {
      try {
        if (!localStorage.getItem(STORAGE_KEY)) {
          setTheme(e.matches ? DARK : LIGHT);
        }
      } catch {
        setTheme(e.matches ? DARK : LIGHT);
      }
    };
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme((prev) => (prev === DARK ? LIGHT : DARK));
  }, []);

  const isDark = theme === DARK;

  return (
    <ThemeContext.Provider value={{ theme, isDark, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

/**
 * useTheme — convenience hook that exposes { theme, isDark, toggleTheme }.
 */
export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (ctx === undefined) {
    throw new Error("useTheme must be used within a <ThemeProvider>");
  }
  return ctx;
}

export default ThemeContext;
